import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  BedDouble,
  Box,
  ChevronDown,
  CircleDot,
  CookingPot,
  Cross,
  Droplets,
  Hammer,
  LockKeyhole,
  Minus,
  Maximize2,
  Mountain,
  Pickaxe,
  Plus,
  Radio,
  RotateCcw,
  Shield,
  Sparkles,
  Sprout,
  Target,
  Wrench,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  WheelEvent as ReactWheelEvent,
} from "react";

import { formatDuration } from "../lib/format";
import {
  BUNKER_GRID,
  BUNKER_WORLD,
  builtFootprintForSpace,
  buildBunkerRoutes,
  connectionPortsBySpace,
  nestedFootprintPercent,
  spaceFootprint,
  spaceRect,
  type ConnectionPort,
} from "../lib/bunkerGeometry";
import type { Catalog, GameState, Space, SpaceFootprint, WorkTask } from "../types";
import type { PopulationPlacement } from "../lib/bunkerPopulation";
import { roomLabourDefaults, roomStaffingProjection } from "../lib/scheduling";
import { SurvivorFigure } from "./SurvivorFigure";
import { BunkerConnectivity } from "./BunkerConnectivity";
import { BunkerPopulationLayer, useBunkerPopulation } from "./BunkerPopulationLayer";

interface RoomArtDefinition {
  image: string | null;
  position: string;
  activity:
    | "watch"
    | "power"
    | "rest"
    | "stores"
    | "cook"
    | "fabricate"
    | "care"
    | "signals"
    | "weapons"
    | "filter"
    | "grow"
    | "guard"
    | "salvage"
    | "general";
}

const roomAsset = (type: string) => `/static/assets/rooms/${type}-operational-v2.webp`;

/**
 * The room key is the stable content identifier shared with the backend.
 * Artwork uses one painted operational plate, then composes damage, work,
 * lighting, site colour, and deterministic dressing as independent layers.
 */
export const ROOM_ART_MANIFEST: Record<string, RoomArtDefinition> = {
  entrance: { image: roomAsset("entrance"), position: "50% 52%", activity: "watch" },
  generator: { image: roomAsset("generator"), position: "52% 52%", activity: "power" },
  dormitory: { image: roomAsset("dormitory"), position: "50% 57%", activity: "rest" },
  storage: { image: roomAsset("storage"), position: "51% 55%", activity: "stores" },
  kitchen: { image: roomAsset("kitchen"), position: "50% 55%", activity: "cook" },
  workshop: { image: roomAsset("workshop"), position: "50% 55%", activity: "fabricate" },
  infirmary: { image: roomAsset("infirmary"), position: "50% 53%", activity: "care" },
  radio: { image: roomAsset("radio"), position: "50% 52%", activity: "signals" },
  armoury: { image: roomAsset("armoury"), position: "50% 54%", activity: "weapons" },
  water_recycler: { image: roomAsset("water_recycler"), position: "50% 54%", activity: "filter" },
  hydroponics: { image: roomAsset("hydroponics"), position: "50% 54%", activity: "grow" },
  security_post: { image: roomAsset("security_post"), position: "54% 52%", activity: "guard" },
  reclamation_shaft: { image: "/static/assets/rooms/reclamation_shaft-operational-v2.webp", position: "50% 54%", activity: "salvage" },
};

const FALLBACK_ROOM_ART: RoomArtDefinition = {
  image: null,
  position: "50% 50%",
  activity: "general",
};

const roomIcons: Record<string, typeof Shield> = {
  entrance: Shield,
  generator: Zap,
  dormitory: BedDouble,
  storage: Box,
  kitchen: CookingPot,
  workshop: Wrench,
  infirmary: Cross,
  radio: Radio,
  armoury: Target,
  water_recycler: Droplets,
  hydroponics: Sprout,
  security_post: Shield,
  reclamation_shaft: Pickaxe,
};

const ABSOLUTE_MIN_ZOOM = 0.25;
const FOCUS_MIN_ZOOM = 0.8;
const MAX_ZOOM = 2.2;
const ZOOM_STEP = 0.2;
const PAN_STEP = 72;

interface CameraState {
  scale: number;
  x: number;
  y: number;
}

interface PointerPosition {
  x: number;
  y: number;
}

interface PinchGesture {
  camera: CameraState;
  centerX: number;
  centerY: number;
  distance: number;
}

export function constrainCameraAxis(
  translation: number,
  viewportSize: number,
  logicalSize: number,
  contentPercent: number,
  scale: number,
): number {
  const viewportOrigin = viewportSize / 2;
  const logicalOrigin = logicalSize / 2;
  const contentSize = logicalSize * contentPercent / 100;
  const start = viewportOrigin + (0 - logicalOrigin) * scale;
  const end = viewportOrigin + (contentSize - logicalOrigin) * scale;
  if (end - start <= viewportSize) return viewportSize / 2 - (start + end) / 2;
  return Math.max(viewportSize - end, Math.min(-start, translation));
}

type RoomVisualState = "operational" | "damaged" | "trashed" | "maintenance" | "construction" | "expand" | "upgrade" | "repair" | "clear";

interface BunkerViewProps {
  state: GameState;
  catalog: Catalog;
  selectedSpaceId: string;
  onSelectSpace: (spaceId: string) => void;
  compact?: boolean;
  controlsOpen?: boolean;
  onToggleControls?: () => void;
}

function stableVisualHash(seed: number, ...parts: string[]): number {
  const source = [String(seed), ...parts].join(":");
  let hash = 2166136261;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function getRoomDetailVariant(seed: number, spaceId: string, roomType: string): number {
  return (stableVisualHash(seed, spaceId, roomType) % 4) + 1;
}

function builtFootprint(space: Space, task?: WorkTask): SpaceFootprint {
  return builtFootprintForSpace(space, task?.target_footprint ?? space.room?.footprint);
}

function ReservedBayGrid({ capacity, occupied }: { capacity: SpaceFootprint; occupied?: SpaceFootprint }) {
  return (
    <span
      className="reserved-bay-grid"
      data-reserved-units={capacity.width * capacity.height - (occupied ? occupied.width * occupied.height : 0)}
      style={{
        "--capacity-width": capacity.width,
        "--capacity-height": capacity.height,
        "--bay-seam-x": `${BUNKER_GRID.seamX / 100 * BUNKER_WORLD.width}px`,
        "--bay-seam-y": `${BUNKER_GRID.seamY / 100 * BUNKER_WORLD.height}px`,
      } as CSSProperties}
      aria-hidden="true"
    >
      {Array.from({ length: capacity.width * capacity.height }, (_, index) => {
        const x = index % capacity.width;
        const y = Math.floor(index / capacity.width);
        const inUse = Boolean(occupied && x < occupied.width && y < occupied.height);
        return <i className={inUse ? "bay-in-use" : "bay-reserved"} key={`${x}-${y}`} />;
      })}
    </span>
  );
}

function RoomPeople({
  placements,
}: {
  placements: PopulationPlacement[];
}) {
  if (!placements.length) return null;
  const visible = placements.slice(0, 4);
  const overflow = placements.length - visible.length;

  return (
    <div className="room-people" aria-hidden="true">
      {visible.map((placement) => {
        const personStyle = {
          "--person-column": placement.slot % 4,
          "--person-row": Math.floor(placement.slot / 4),
        } as CSSProperties;
        return (
          <span
            className="room-figure-slot"
            data-activity={placement.activity}
            data-motion={placement.motion}
            data-survivor-id={placement.survivor.id}
            key={placement.survivor.id}
            style={personStyle}
          >
            <SurvivorFigure
              survivor={placement.survivor}
              activity={placement.activity}
              className="room-survivor-figure"
              facing={placement.facing}
              lod="full"
              motion={placement.motion}
              prop={placement.prop}
            />
          </span>
        );
      })}
      {overflow > 0 && <span className="room-population-overflow">+{overflow}</span>}
    </div>
  );
}

function roomTierForFootprint(footprint: SpaceFootprint): "compact" | "standard" | "expanded" {
  const units = footprint.width * footprint.height;
  if (units <= 1) return "compact";
  if (units <= 2) return "standard";
  return "expanded";
}

function roomFillRatio(roomType: string, state: GameState): number {
  const resourcesByRoom: Record<string, Array<keyof GameState["resources"]>> = {
    entrance: ["ammunition"],
    generator: ["fuel"],
    dormitory: ["food", "water"],
    storage: ["food", "water", "medicine", "fuel", "ammunition", "scrap", "components"],
    kitchen: ["food", "water"],
    workshop: ["scrap", "components"],
    infirmary: ["medicine"],
    radio: ["components"],
    armoury: ["ammunition"],
    water_recycler: ["water"],
    hydroponics: ["food", "water"],
    security_post: ["ammunition"],
    reclamation_shaft: ["scrap"],
  };
  const resources = resourcesByRoom[roomType] ?? [];
  const capacity = resources.reduce((total, key) => total + Math.max(0, state.resource_capacity?.[key] ?? 0), 0);
  if (!capacity) return 0;
  const stored = resources.reduce((total, key) => total + Math.max(0, state.resources?.[key] ?? 0), 0);
  return Math.max(0, Math.min(1, stored / capacity));
}

/**
 * Every structural bay receives a room-specific workstation or storage prop.
 * This makes footprint progression legible even when the authored hero plate
 * is being composited into a neutral extension shell.
 */
function RoomCapacityDressing({ roomType, footprint }: { roomType: string; footprint: SpaceFootprint }) {
  const units = footprint.width * footprint.height;
  return (
    <div
      className="room-capacity-props"
      aria-hidden="true"
      data-capacity-props={units}
      style={{ "--prop-columns": footprint.width, "--prop-rows": footprint.height } as CSSProperties}
    >
      {Array.from({ length: units }, (_, index) => (
        <i
          className="capacity-prop"
          data-bay-column={index % footprint.width}
          data-bay-row={Math.floor(index / footprint.width)}
          key={`${roomType}-${index}`}
          style={{
            "--prop-column": index % footprint.width,
            "--prop-row": Math.floor(index / footprint.width),
            "--prop-columns": footprint.width,
            "--prop-rows": footprint.height,
          } as CSSProperties}
        ><span /><b /></i>
      ))}
    </div>
  );
}

function RoomScene({
  space,
  state,
  catalog,
  roomType,
  visualState,
  condition,
  population,
  task,
}: {
  space: Space;
  state: GameState;
  catalog: Catalog;
  roomType: string;
  visualState: RoomVisualState;
  condition: number;
  population: PopulationPlacement[];
  task?: WorkTask;
}) {
  const Icon = roomIcons[roomType] ?? CircleDot;
  const definition = catalog.rooms[roomType];
  const art = ROOM_ART_MANIFEST[roomType] ?? FALLBACK_ROOM_ART;
  const visualHash = stableVisualHash(state.site.visual_seed, space.id, roomType);
  const capacity = spaceFootprint(space);
  const footprint = builtFootprint(space, task);
  const tier = roomTierForFootprint(footprint);
  const units = footprint.width * footprint.height;
  const fillRatio = roomFillRatio(roomType, state);
  const detailVariant = getRoomDetailVariant(state.site.visual_seed, space.id, roomType);
  const staffing = space.room ? roomStaffingProjection(space.room) : null;
  const criticalShortfall = Boolean(
    space.room
    && (space.room.labour_priority ?? roomLabourDefaults(roomType).priority) === "critical"
    && staffing?.status === "uncovered",
  );
  const activityActive = visualState === "operational" && (
    roomType !== "reclamation_shaft"
    || (
      space.room?.operation?.powered === true
      && space.room.operation.blocked_reason === null
    )
  );
  const damageOpacity = Math.min(0.92, Math.max(0.16, (100 - condition) / 100));
  const artStyle = {
    "--room-art-image": art.image ? `url("${art.image}")` : "none",
    "--room-art-position": art.position,
    "--room-damage-opacity": damageOpacity.toFixed(2),
    "--detail-x": `${9 + (visualHash % 67)}%`,
    "--detail-y": `${5 + ((visualHash >>> 7) % 19)}%`,
    "--detail-scale": (0.86 + ((visualHash >>> 12) % 24) / 100).toFixed(2),
    "--room-fill": fillRatio.toFixed(3),
    "--room-fill-scale": (0.62 + fillRatio * 0.38).toFixed(3),
    "--room-fill-percent": `${Math.round(fillRatio * 100)}%`,
    "--room-stock-height": `${Math.round(26 + fillRatio * 42)}%`,
    "--room-growth-height": `${Math.round(32 + fillRatio * 55)}%`,
  } as CSSProperties;
  const footprintPercent = nestedFootprintPercent(footprint, capacity);
  const footprintStyle = {
    "--built-width": `${footprintPercent.width}%`,
    "--built-height": `${footprintPercent.height}%`,
  } as CSSProperties;
  const stateLabel =
    visualState === "construction"
      ? "building"
      : visualState === "expand"
        ? "expanding"
      : visualState === "repair"
        ? "repairing"
        : visualState === "clear"
          ? "clearing"
          : visualState === "operational"
            ? null
            : visualState;

  return (
    <div
      className={`room-built-footprint footprint-${footprint.width}x${footprint.height} tier-${tier} ${footprint.width === capacity.width ? "fills-chamber-width" : ""} ${footprint.height === capacity.height ? "fills-chamber-height" : ""}`}
      data-room-footprint={`${footprint.width}x${footprint.height}`}
      data-room-tier={tier}
      data-room-units={units}
      style={footprintStyle}
    >
      <div className="room-ceiling art-room-frame" aria-hidden="true"><i /><i /><i /></div>
      <div
        className={`room-interior room-${roomType} art-room art-state-${visualState} detail-${detailVariant}`}
        data-activity={art.activity}
        data-activity-active={activityActive ? "true" : "false"}
        data-detail-variant={detailVariant}
        style={artStyle}
      >
        <div className="room-fallback" aria-hidden="true">
          <div className="room-main-prop"><Icon size={28} strokeWidth={1.35} /></div>
          <div className="room-clutter"><i /><i /><i /></div>
          <div className="room-floor-line" />
        </div>
        <div className="room-extension-shell" aria-hidden="true" />
        <div className="room-art-plate" aria-hidden="true" />
        <div className="room-art-wash" aria-hidden="true" />
        <RoomCapacityDressing roomType={roomType} footprint={footprint} />
        <div className="room-bay-seams" aria-hidden="true">
          {Array.from({ length: Math.max(0, footprint.width - 1) }, (_, index) => (
            <i
              className="bay-seam-vertical"
              key={`vertical-${index + 1}`}
              style={{ left: `${((index + 1) / footprint.width) * 100}%` }}
            />
          ))}
          {Array.from({ length: Math.max(0, footprint.height - 1) }, (_, index) => (
            <i
              className="bay-seam-horizontal"
              key={`horizontal-${index + 1}`}
              style={{ top: `${((index + 1) / footprint.height) * 100}%` }}
            />
          ))}
        </div>
        <div className="room-light" aria-hidden="true"><span /></div>
        <div className="room-dressing" aria-hidden="true">
          <i className="dressing-crate" />
          <i className="dressing-pipe" />
          <i className="dressing-cable" />
          <i className="dressing-puddle" />
        </div>
        <div className="room-activity-fx" aria-hidden="true"><i /><i /><i /></div>
        <div className="room-state-overlay" aria-hidden="true">
          <i className="state-scaffold" />
          <i className="state-crack" />
          <i className="state-sparks" />
          <i className="state-smoke" />
          <i className="state-debris" />
        </div>
      </div>
      <RoomPeople placements={population} />
      <div className="room-label">
        <Icon size={13} aria-hidden="true" />
        <span>{definition?.short_name ?? roomType.replaceAll("_", " ")}</span>
        {stateLabel && <em>{stateLabel}</em>}
        {criticalShortfall && <em className="labour-shortfall-label">critical post uncovered</em>}
      </div>
      <div className={`condition-line ${visualState === "construction" ? "construction-line" : ""}`}>
        <span style={visualState === "construction" ? undefined : { width: `${condition}%` }} />
      </div>
    </div>
  );
}

function SpaceCell({
  space,
  state,
  catalog,
  population,
  selected,
  linkedSides,
  onSelect,
}: {
  space: Space;
  state: GameState;
  catalog: Catalog;
  population: PopulationPlacement[];
  selected: boolean;
  linkedSides: ConnectionPort[];
  onSelect: () => void;
}) {
  const task = state.tasks.find((item) => item.space_id === space.id);
  const rect = spaceRect(space);
  const footprint = spaceFootprint(space);
  const occupiedFootprint = space.room || (task?.kind === "build" && task.room_type)
    ? builtFootprint(space, task)
    : undefined;
  const style = {
    "--space-left": `${rect.left}%`,
    "--space-top": `${rect.top}%`,
    "--space-render-width": `${rect.width}%`,
    "--space-render-height": `${rect.height}%`,
    "--footprint-width": footprint.width,
    "--footprint-height": footprint.height,
  } as CSSProperties;

  if (!space.discovered) {
    return <div className="undiscovered-space" data-footprint={`${footprint.width}x${footprint.height}`} style={style} aria-hidden="true" />;
  }

  const taskClass = task ? `task-${task.kind}` : "";
  const roomVisualState: RoomVisualState | null = space.room
    ? task?.kind === "expand"
      ? "expand"
      : task?.kind === "upgrade"
        ? "upgrade"
      : task?.kind === "repair"
      ? "repair"
      : task?.kind === "clear"
        ? "clear"
        : space.room.status === "expanding"
          ? "expand"
          : space.room.status === "upgrading"
            ? "upgrade"
          : space.room.status
    : null;
  const taskRoomName = task?.room_type
    ? catalog.rooms[task.room_type]?.short_name ?? task.room_type.replaceAll("_", " ")
    : space.room
      ? catalog.rooms[space.room.type]?.short_name ?? space.room.type.replaceAll("_", " ")
      : null;
  const taskTarget = task ? builtFootprint(space, task) : null;
  const remaining = task ? `${formatDuration(task.seconds_left)} remaining` : null;
  const operationClass = !space.room
    ? ""
    : space.room.enabled === false
      ? "room-disabled"
      : ["load_shed", "no_power", "no_fuel"].includes(space.room.operation?.blocked_reason ?? "")
        ? "room-shed"
        : "";
  const labourPriority = space.room?.labour_priority ?? (space.room ? roomLabourDefaults(space.room.type).priority : "normal");
  const labourUncovered = Boolean(
    space.room
    && labourPriority === "critical"
    && roomStaffingProjection(space.room).status === "uncovered",
  );
  const baseAccessibilityLabel = task?.kind === "build" && taskRoomName && taskTarget
    ? `Building ${taskRoomName}, ${taskTarget.width} × ${taskTarget.height}, ${remaining}`
    : task?.kind === "expand" && taskRoomName && taskTarget
      ? `Expanding ${taskRoomName} to ${taskTarget.width} × ${taskTarget.height}, ${remaining}`
      : task?.kind === "upgrade" && taskRoomName
        ? `Upgrading ${taskRoomName} to installation level ${task.target_installation_level ?? 2}, ${remaining}`
      : task?.kind === "excavate"
        ? `Excavating ${space.material}, ${remaining}`
        : task?.kind === "repair" && taskRoomName
          ? `Repairing ${taskRoomName}, ${remaining}`
          : task?.kind === "maintenance" && taskRoomName
            ? `Maintaining ${taskRoomName}, ${remaining}`
          : task?.kind === "clear" && taskRoomName
            ? `Clearing ruined ${taskRoomName}, ${remaining}`
            : space.room
              ? `${catalog.rooms[space.room.type]?.name ?? space.room.type}, ${space.room.status}`
              : `${space.material} ${space.state} chamber`;
  const accessibilityLabel = `${baseAccessibilityLabel}${labourUncovered ? ", critical post uncovered" : ""}`;

  return (
    <button
      className={`bunker-space footprint-${footprint.width}x${footprint.height} size-${space.size} state-${space.state} ${space.room ? `has-room status-${space.room.status}` : "empty-space"} ${operationClass} ${labourUncovered ? "room-labour-uncovered" : ""} ${taskClass} ${selected ? "selected" : ""}`}
      data-footprint={`${footprint.width}x${footprint.height}`}
      data-room-type={space.room?.type ?? task?.room_type}
      data-site-space-id={space.id}
      style={style}
      onClick={onSelect}
      aria-pressed={selected}
      aria-controls="space-inspector"
      aria-label={accessibilityLabel}
    >
      <ReservedBayGrid capacity={footprint} occupied={occupiedFootprint} />
      <span className="room-ports" aria-hidden="true">
        {linkedSides.map((port) => (
          <i
            className={`room-port port-${port.side}`}
            data-connection-id={port.id}
            data-shared-wall={port.sharedWall ? "true" : "false"}
            key={port.id}
            style={{ "--port-offset": `${port.offset}%` } as CSSProperties}
          />
        ))}
      </span>
      {space.room && roomVisualState ? (
        <RoomScene
          space={space}
          state={state}
          catalog={catalog}
          roomType={space.room.type}
          visualState={roomVisualState}
          condition={space.room.condition}
          population={population}
          task={task}
        />
      ) : task?.kind === "build" && task.room_type ? (
        <RoomScene
          space={space}
          state={state}
          catalog={catalog}
          roomType={task.room_type}
          visualState="construction"
          condition={100}
          population={population}
          task={task}
        />
      ) : space.state === "sealed" ? (
        <div className="sealed-face">
          <Mountain size={25} aria-hidden="true" />
          <strong>{space.material}</strong>
          <small>{space.hazard ? `Possible ${space.hazard}` : "No known hazard"}</small>
          <LockKeyhole size={13} aria-hidden="true" />
          <RoomPeople placements={population} />
        </div>
      ) : (
        <div className="empty-chamber">
          <Sparkles size={20} aria-hidden="true" />
          <strong>Cleared chamber</strong>
          <small>{space.size} footprint</small>
          <RoomPeople placements={population} />
        </div>
      )}
      {task && (
        <div className="space-task">
          <Hammer size={13} aria-hidden="true" />
          <span>{task.kind === "build" ? `Building ${task.room_type}` : task.kind}</span>
          <strong>{formatDuration(task.seconds_left)}</strong>
        </div>
      )}
    </button>
  );
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(
    target.closest("button, a, input, select, textarea, summary, [role='button']"),
  );
}

export function BunkerView({
  state,
  catalog,
  selectedSpaceId,
  onSelectSpace,
  compact = false,
  controlsOpen = true,
  onToggleControls,
}: BunkerViewProps) {
  const [deep, rock, accent, light, warning] = state.site.archetype.palette;
  const entrance = state.site.spaces.find((space) => space.id === "space-entrance");
  const viewportRef = useRef<HTMLElement>(null);
  const [viewportSize, setViewportSize] = useState<{ width: number; height: number }>({ width: BUNKER_WORLD.width, height: BUNKER_WORLD.height });
  const safeTop = compact ? 58 : 0;
  const safeBottom = compact ? 54 : 0;
  const usableViewportHeight = Math.max(120, viewportSize.height - safeTop - safeBottom);
  const worldBounds = useMemo(() => state.site.spaces.reduce(
    (bounds, space) => {
      const rect = spaceRect(space);
      return { right: Math.max(bounds.right, rect.right + 1.5), bottom: Math.max(bounds.bottom, rect.bottom + 2) };
    },
    { right: 100, bottom: 100 },
  ), [state.site.spaces]);
  const overviewScale = Math.max(
    ABSOLUTE_MIN_ZOOM,
    Math.min(
      1,
      (viewportSize.width * .94) / (BUNKER_WORLD.width * worldBounds.right / 100),
      (usableViewportHeight * .9) / (BUNKER_WORLD.height * worldBounds.bottom / 100),
    ),
  );
  const focusScale = Math.max(viewportSize.width <= 600 ? FOCUS_MIN_ZOOM : 1, overviewScale);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    intent: "pending" | "pan" | "scroll";
  } | null>(null);
  const suppressClickRef = useRef(false);
  const activePointersRef = useRef<Map<number, PointerPosition>>(new Map());
  const pinchRef = useRef<PinchGesture | null>(null);
  const [camera, setCamera] = useState<CameraState>({ scale: focusScale, x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const routeGeometry = useMemo(() => {
    const routes = buildBunkerRoutes(state.site.spaces, state.site.connections);
    return { routes, portsBySpace: connectionPortsBySpace(routes, state.site.spaces) };
  }, [state.site.connections, state.site.spaces]);
  const population = useBunkerPopulation(state, routeGeometry.routes);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const updateSize = () => {
      const bounds = viewport.getBoundingClientRect();
      if (bounds.width > 0 && bounds.height > 0) setViewportSize({ width: bounds.width, height: bounds.height });
    };
    updateSize();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateSize);
      return () => window.removeEventListener("resize", updateSize);
    }
    const observer = new ResizeObserver(updateSize);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setCamera((previous) => {
      const next = constrainCamera({ ...previous, scale: Math.max(previous.scale, overviewScale) });
      return Math.abs(next.scale - previous.scale) < .001
        && Math.abs(next.x - previous.x) < .5
        && Math.abs(next.y - previous.y) < .5
        ? previous
        : next;
    });
  }, [overviewScale, viewportSize.width, viewportSize.height]);

  useEffect(() => {
    const selected = state.site.spaces.find((space) => space.id === selectedSpaceId);
    const rect = selected ? spaceRect(selected) : { centerX: 50, centerY: 50 };
    setCamera(cameraCentredOn(focusScale, rect.centerX, rect.centerY));
    setDragging(false);
    dragRef.current = null;
    activePointersRef.current.clear();
    pinchRef.current = null;
  }, [state.site.visual_seed, focusScale, worldBounds.right, worldBounds.bottom]);

  useEffect(() => {
    const selected = state.site.spaces.find((space) => space.id === selectedSpaceId);
    const bounds = viewportRef.current?.getBoundingClientRect();
    if (!selected || !bounds) return;
    const rect = spaceRect(selected);
    setCamera((previous) => {
      const screenX = bounds.width / 2 + (rect.centerX / 100 * BUNKER_WORLD.width - BUNKER_WORLD.width / 2) * previous.scale + previous.x;
      const screenY = bounds.height / 2 + (safeTop - safeBottom) / 2 + (rect.centerY / 100 * BUNKER_WORLD.height - BUNKER_WORLD.height / 2) * previous.scale + previous.y;
      const outside = screenX < bounds.width * .12 || screenX > bounds.width * .88
        || screenY < safeTop + usableViewportHeight * .12
        || screenY > safeTop + usableViewportHeight * .88;
      return outside ? cameraCentredOn(previous.scale, rect.centerX, rect.centerY) : previous;
    });
  }, [selectedSpaceId, safeBottom, safeTop, usableViewportHeight]);

  const entranceRect = entrance ? spaceRect(entrance) : null;
  const siteStyle = {
    "--site-deep": deep,
    "--site-rock": rock,
    "--site-accent": accent,
    "--site-light": light,
    "--site-warning": warning,
    "--entrance-center-x": `${entranceRect?.centerX ?? 50}%`,
    "--world-bottom": `${worldBounds.bottom}%`,
    "--world-right": `${worldBounds.right}%`,
  } as CSSProperties;

  function constrainCamera(next: CameraState): CameraState {
    const bounds = viewportRef.current?.getBoundingClientRect();
    const scale = Math.max(overviewScale, Math.min(MAX_ZOOM, next.scale));
    if (!bounds) return { ...next, scale };

    return {
      scale,
      x: constrainCameraAxis(next.x, bounds.width, BUNKER_WORLD.width, worldBounds.right, scale),
      y: constrainCameraAxis(next.y, bounds.height, BUNKER_WORLD.height, worldBounds.bottom, scale),
    };
  }

  function cameraCentredOn(scale: number, worldX: number, worldY: number): CameraState {
    const bounds = viewportRef.current?.getBoundingClientRect();
    if (!bounds) return { scale, x: 0, y: 0 };
    return constrainCamera({
      scale,
      x: (50 - worldX) / 100 * BUNKER_WORLD.width * scale,
      y: (50 - worldY) / 100 * BUNKER_WORLD.height * scale + (safeTop - safeBottom) / 2,
    });
  }

  function changeZoom(delta: number, clientX?: number, clientY?: number) {
    const bounds = viewportRef.current?.getBoundingClientRect();
    const selectedBounds = clientX === undefined
      ? viewportRef.current?.querySelector<HTMLElement>(".bunker-space.selected")?.getBoundingClientRect()
      : undefined;
    setCamera((previous) => {
      const scale = Math.max(overviewScale, Math.min(MAX_ZOOM, previous.scale + delta));
      if (scale === previous.scale) return previous;
      const ratio = scale / previous.scale;
      const anchorX = bounds
        ? clientX !== undefined
          ? clientX - bounds.left - bounds.width / 2
          : selectedBounds
            ? selectedBounds.left + selectedBounds.width / 2 - bounds.left - bounds.width / 2
            : 0
        : 0;
      const anchorY = bounds
        ? clientY !== undefined
          ? clientY - bounds.top - bounds.height / 2
          : selectedBounds
            ? selectedBounds.top + selectedBounds.height / 2 - bounds.top - bounds.height / 2
            : 0
        : 0;
      return constrainCamera({
        scale,
        x: previous.x * ratio + anchorX * (1 - ratio),
        y: previous.y * ratio + anchorY * (1 - ratio),
      });
    });
  }

  function panBy(x: number, y: number) {
    setCamera((previous) => constrainCamera({ ...previous, x: previous.x + x, y: previous.y + y }));
  }

  function resetCamera() {
    const selected = state.site.spaces.find((space) => space.id === selectedSpaceId);
    const rect = selected ? spaceRect(selected) : { centerX: 50, centerY: 50 };
    setCamera(cameraCentredOn(focusScale, rect.centerX, rect.centerY));
  }

  function fitOverview() {
    setCamera(cameraCentredOn(overviewScale, worldBounds.right / 2, worldBounds.bottom / 2));
  }

  function handleWheel(event: ReactWheelEvent<HTMLElement>) {
    if (event.deltaY === 0 || (!event.ctrlKey && !event.metaKey)) return;
    const delta = event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
    const atLimit = delta > 0
      ? camera.scale >= MAX_ZOOM
      : camera.scale <= overviewScale;
    if (atLimit) return;
    event.preventDefault();
    changeZoom(delta, event.clientX, event.clientY);
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLElement>) {
    if (event.pointerType !== "touch" && isInteractiveTarget(event.target)) return;
    if (event.pointerType === "touch") {
      activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const pointers = [...activePointersRef.current.entries()];
      if (pointers.length >= 2) {
        event.preventDefault();
        const [, first] = pointers[0]!;
        const [, second] = pointers[1]!;
        pinchRef.current = {
          camera,
          centerX: (first.x + second.x) / 2,
          centerY: (first.y + second.y) / 2,
          distance: Math.max(1, Math.hypot(second.x - first.x, second.y - first.y)),
        };
        dragRef.current = null;
        suppressClickRef.current = true;
        if (typeof event.currentTarget.setPointerCapture === "function") {
          for (const [pointerId] of pointers) {
            try { event.currentTarget.setPointerCapture(pointerId); } catch { /* Pointer may already be native-scrolling. */ }
          }
        }
        setDragging(true);
      } else {
        dragRef.current = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          originX: camera.x,
          originY: camera.y,
          intent: "pending",
        };
      }
      return;
    }
    if (!canPan) return;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: camera.x,
      originY: camera.y,
      intent: "pan",
    };
    if (typeof event.currentTarget.setPointerCapture === "function") {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    setDragging(true);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLElement>) {
    if (event.pointerType === "touch" && activePointersRef.current.has(event.pointerId)) {
      activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const pointers = [...activePointersRef.current.values()];
      const gesture = pinchRef.current;
      const bounds = viewportRef.current?.getBoundingClientRect();
      if (pointers.length >= 2 && gesture && bounds) {
        event.preventDefault();
        const first = pointers[0]!;
        const second = pointers[1]!;
        const centerX = (first.x + second.x) / 2;
        const centerY = (first.y + second.y) / 2;
        const distance = Math.max(1, Math.hypot(second.x - first.x, second.y - first.y));
        const scale = Math.max(overviewScale, Math.min(MAX_ZOOM, gesture.camera.scale * distance / gesture.distance));
        const ratio = scale / gesture.camera.scale;
        const anchorX = gesture.centerX - bounds.left - bounds.width / 2;
        const anchorY = gesture.centerY - bounds.top - bounds.height / 2;
        setCamera(constrainCamera({
          scale,
          x: gesture.camera.x * ratio + anchorX * (1 - ratio) + centerX - gesture.centerX,
          y: gesture.camera.y * ratio + anchorY * (1 - ratio) + centerY - gesture.centerY,
        }));
        return;
      }
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      const deltaX = event.clientX - drag.startX;
      const deltaY = event.clientY - drag.startY;
      if (drag.intent === "pending" && Math.hypot(deltaX, deltaY) >= 8) {
        if (Math.abs(deltaY) > Math.abs(deltaX) * 1.08) {
          drag.intent = "scroll";
          setDragging(false);
          return;
        }
        drag.intent = "pan";
        suppressClickRef.current = true;
        if (typeof event.currentTarget.setPointerCapture === "function") {
          try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* Gesture may have been cancelled by the browser. */ }
        }
        setDragging(true);
      }
      if (drag.intent !== "pan") return;
      event.preventDefault();
      setCamera((previous) => constrainCamera({
        ...previous,
        x: drag.originX + deltaX,
        y: drag.originY + deltaY,
      }));
      return;
    }
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setCamera((previous) => constrainCamera({
      ...previous,
      x: drag.originX + event.clientX - drag.startX,
      y: drag.originY + event.clientY - drag.startY,
    }));
  }

  function endPointerDrag(event: ReactPointerEvent<HTMLElement>) {
    const wasTouch = event.pointerType === "touch" && activePointersRef.current.has(event.pointerId);
    if (!wasTouch && dragRef.current?.pointerId !== event.pointerId) return;
    if (wasTouch) activePointersRef.current.delete(event.pointerId);
    pinchRef.current = null;
    const remaining = [...activePointersRef.current.entries()][0];
    dragRef.current = remaining ? {
      pointerId: remaining[0],
      startX: remaining[1].x,
      startY: remaining[1].y,
      originX: camera.x,
      originY: camera.y,
      intent: "pending",
    } : null;
    if (typeof event.currentTarget.hasPointerCapture === "function" && event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setDragging(false);
    if (!remaining && suppressClickRef.current) {
      window.setTimeout(() => { suppressClickRef.current = false; }, 0);
    }
  }

  function handleClickCapture(event: ReactMouseEvent<HTMLElement>) {
    if (!suppressClickRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    suppressClickRef.current = false;
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.target !== event.currentTarget) return;
    const actions: Record<string, () => void> = {
      ArrowLeft: () => panBy(PAN_STEP, 0),
      ArrowRight: () => panBy(-PAN_STEP, 0),
      ArrowUp: () => panBy(0, PAN_STEP),
      ArrowDown: () => panBy(0, -PAN_STEP),
      "+": () => changeZoom(ZOOM_STEP),
      "=": () => changeZoom(ZOOM_STEP),
      "-": () => changeZoom(-ZOOM_STEP),
      "0": resetCamera,
      Home: fitOverview,
    };
    const action = actions[event.key];
    if (!action) return;
    event.preventDefault();
    action();
  }

  const cameraStyle = {
    height: `${BUNKER_WORLD.height}px`,
    marginLeft: `${-BUNKER_WORLD.width / 2}px`,
    marginTop: `${-BUNKER_WORLD.height / 2}px`,
    transform: `translate3d(${camera.x}px, ${camera.y}px, 0) scale(${camera.scale})`,
    width: `${BUNKER_WORLD.width}px`,
  } as CSSProperties;
  const canPan = BUNKER_WORLD.width * worldBounds.right / 100 * camera.scale > viewportSize.width + 1
    || BUNKER_WORLD.height * worldBounds.bottom / 100 * camera.scale > usableViewportHeight + 1;
  const selectedCameraSpace = state.site.spaces.find((space) => space.id === selectedSpaceId);
  const selectedCameraRect = selectedCameraSpace ? spaceRect(selectedCameraSpace) : { centerX: 50, centerY: 50 };
  const resetTarget = cameraCentredOn(focusScale, selectedCameraRect.centerX, selectedCameraRect.centerY);
  const fitTarget = cameraCentredOn(overviewScale, worldBounds.right / 2, worldBounds.bottom / 2);
  const isReset = Math.abs(camera.scale - resetTarget.scale) < .001
    && Math.abs(camera.x - resetTarget.x) < 1
    && Math.abs(camera.y - resetTarget.y) < 1;
  const isFit = Math.abs(camera.scale - fitTarget.scale) < .001
    && Math.abs(camera.x - fitTarget.x) < 1
    && Math.abs(camera.y - fitTarget.y) < 1;
  const spatialSpaces = [...state.site.spaces].sort(
    (left, right) => left.y - right.y || left.x - right.x || left.id.localeCompare(right.id),
  );
  const completedResearchEras = (state.research?.completed ?? [])
    .map((projectId) => catalog.research[projectId]?.era ?? 0);
  const visualEra = Math.max(0, ...completedResearchEras);

  return (
    <section
      className={`bunker-view ${compact ? "bunker-view-compact" : ""} site-${state.site.archetype.key} geology-${state.site.geology.key} history-${state.site.history.key} progress-era-${visualEra} ${dragging ? "camera-dragging" : ""} ${canPan ? "camera-can-pan" : ""} power-${state.site.power}`}
      style={siteStyle}
      aria-label="Side-on bunker cutaway"
      aria-describedby="bunker-camera-help"
      onClickCapture={handleClickCapture}
      onKeyDown={handleKeyDown}
      onPointerCancel={endPointerDrag}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endPointerDrag}
      onWheel={handleWheel}
      ref={viewportRef}
      tabIndex={0}
    >
      <p className="visually-hidden" id="bunker-camera-help">
        Drag or use the arrow keys to pan. On touch screens, swipe horizontally to move the camera, swipe vertically to continue down the page, and pinch with two fingers to zoom and pan. Use plus and minus to zoom, zero to focus the selected chamber, and Home to fit the whole bunker. Hold Control or Command while scrolling to zoom. Room buttons remain keyboard selectable.
      </p>
      <div className="bunker-camera" style={cameraStyle}>
        <div className="weather-layer" aria-hidden="true">
          <i /><i /><i /><i /><i /><i />
        </div>
        <div className="surface-layer" aria-hidden="true">
          <div className="surface-road" /><div className="dead-trees" /><div className="watch-light" />
        </div>
        <div className="earth-cutaway" aria-hidden="true">
          <div className="earth-strata strata-one" />
          <div className="earth-strata strata-two" />
          <div className="earth-strata strata-three" />
        </div>
        <div className="site-history-evidence" aria-hidden="true"><i /><i /><i /></div>
        <div className="bunker-grid">
          <div className="access-shaft" aria-hidden="true"><i /><i /><i /><ChevronDown size={15} /></div>
          <BunkerConnectivity routes={routeGeometry.routes} />
          <BunkerPopulationLayer placements={population.placements} transits={population.transits} />
          {spatialSpaces.map((space) => (
            <SpaceCell
              key={space.id}
              space={space}
              state={state}
              catalog={catalog}
              population={population.stationaryBySpace.get(space.id) ?? []}
              selected={space.id === selectedSpaceId}
              linkedSides={routeGeometry.portsBySpace.get(space.id) ?? []}
              onSelect={() => onSelectSpace(space.id)}
            />
          ))}
        </div>
        <div className="ambient-dust" aria-hidden="true"><i /><i /><i /><i /><i /></div>
      </div>

      <div
        className={`bunker-camera-controls ${controlsOpen ? "expanded" : "collapsed"}`}
        aria-label="Bunker camera controls"
        data-min-touch-target="44"
        role="group"
        style={{ "--camera-hit-target": "36px" } as CSSProperties}
      >
        {onToggleControls && (
          <button
            aria-controls="bunker-camera-control-panel"
            aria-expanded={controlsOpen}
            aria-label={controlsOpen ? "Collapse bunker view controls" : "Expand bunker view controls"}
            className="camera-controls-toggle"
            onClick={onToggleControls}
            type="button"
          >
            <Maximize2 aria-hidden="true" size={14} />
            <span>View</span>
            <output className="camera-zoom-readout" aria-live="polite">{Math.round(camera.scale * 100)}%</output>
          </button>
        )}
        {controlsOpen && (
          <div className="camera-control-panel" id="bunker-camera-control-panel">
            <div className="camera-zoom-controls">
              <button type="button" aria-label="Zoom out" disabled={camera.scale <= overviewScale} onClick={() => changeZoom(-ZOOM_STEP)} title="Zoom out (-)">
                <Minus size={15} />
              </button>
              <button type="button" aria-label="Reset bunker view" disabled={isReset} onClick={resetCamera} title="Reset view (0)">
                <RotateCcw size={14} />
              </button>
              <button type="button" aria-label="Fit whole bunker" disabled={isFit} onClick={fitOverview} title="Fit whole bunker (Home)" data-camera-action="fit-overview">
                <Maximize2 size={14} />
              </button>
              <output className="camera-zoom-readout" aria-live="polite">{Math.round(camera.scale * 100)}%</output>
              <button type="button" aria-label="Zoom in" disabled={camera.scale >= MAX_ZOOM} onClick={() => changeZoom(ZOOM_STEP)} title="Zoom in (+)">
                <Plus size={15} />
              </button>
            </div>
            {!compact && (
              <div className="camera-pan-controls" aria-label="Pan bunker view" role="group">
                <button className="pan-up" type="button" aria-label="Pan up" disabled={!canPan} onClick={() => panBy(0, PAN_STEP)}><ArrowUp size={13} /></button>
                <button className="pan-left" type="button" aria-label="Pan left" disabled={!canPan} onClick={() => panBy(PAN_STEP, 0)}><ArrowLeft size={13} /></button>
                <button className="pan-right" type="button" aria-label="Pan right" disabled={!canPan} onClick={() => panBy(-PAN_STEP, 0)}><ArrowRight size={13} /></button>
                <button className="pan-down" type="button" aria-label="Pan down" disabled={!canPan} onClick={() => panBy(0, -PAN_STEP)}><ArrowDown size={13} /></button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="cutaway-legend">
        <span><i className="legend-operational" />Operational</span>
        <span><i className="legend-damaged" />Damaged</span>
        <span><i className="legend-sealed" />Sealed</span>
      </div>
    </section>
  );
}
